import { useEffect, useMemo, useState } from 'react'

import type { QuantRunChart } from '@/app/hooks/useQuantRunChart'
import { candleCount, chartToDraw, readoutAt } from '@/app/lib/quant/chartToDraw'
import { truncateChart } from '@/app/lib/quant/chartReplay'
import { BARS_OPTIONS } from '@/app/lib/quant/quantChartBars'
import type { ChartModel } from '@/app/lib/quant/types'
import { Button } from '@/ui/Button'
import { ErrorBoundary } from '@/ui/ErrorBoundary'
import { QuantEquityChart } from '@/ui/QuantEquityChart'
import { QuantChart, type ChartViewCommand, type QuantViewChange } from '@/ui/QuantChart'
import { QuantChartScrubber, type QuantScrubSpeed, type QuantScrubWindow } from '@/ui/QuantChartScrubber'
import { QuantReadout } from '@/ui/QuantReadout'
import { Select } from '@/ui/Select'

type Props = {
  chart: QuantRunChart
  /** The run's stored curve, passed only when it has at least 2 valid points (`hasStoredEquity`). */
  equityCurve?: ReadonlyArray<readonly [number, number]> | null
  /** The server's `has_chart`. With no chart and a stored curve the panel shows the equity-only chart. */
  hasChart?: boolean
}
type BarsProps = { chart: QuantRunChart }

const BARS_SELECT_OPTIONS = BARS_OPTIONS.map((n) => ({ value: String(n), label: n.toLocaleString('en-US') }))

/** Fixed-height toolbar: the bars Select and the loaded count never move each other. */
function BarsToolbar({ chart }: BarsProps) {
  return (
    <div data-chart-toolbar className="flex h-10 shrink-0 items-center gap-3 border-b border-paper-14 px-7">
      <span className="font-mono text-[10px] tracking-[0.14em] text-paper-40 uppercase">Bars</span>
      <Select
        ariaLabel="Bars"
        value={String(chart.bars)}
        options={BARS_SELECT_OPTIONS}
        onValueChange={(v) => chart.setBars(Number(v))}
        className="w-[120px] shrink-0"
      />
      <span data-bars-loaded className="w-[170px] shrink-0 truncate font-mono text-[11px] text-paper-50 tabular-nums">
        {chart.loadedCount === null ? '' : `${chart.loadedCount.toLocaleString('en-US')} bars loaded`}
      </span>
      <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-paper-40" title={`Up to the last ${chart.bars.toLocaleString('en-US')} bars of the run, plus the levels and zones in that window. A run can contain fewer.`}>
        Up to the last {chart.bars.toLocaleString('en-US')} bars of the run, plus the levels and zones in that window. A run can contain fewer.
      </span>
    </div>
  )
}

/** The run's Chart tab (spec 036 P8): lazy canvas, replay scrubber, window, readout. */
export function QuantRunChartPanel({ chart, equityCurve, hasChart = true }: Props) {
  const stored = equityCurve ?? null
  // Spec 038 20.6 rule 4: no chart (never written, missing, or without candles) but a stored curve.
  // Any other failure (e.g. chart_invalid) keeps the alert with Retry, since a retry may recover.
  const equityOnly =
    stored !== null &&
    (!hasChart ||
      (chart.status === 'ready' && candleCount(chart.model) === 0) ||
      (chart.status === 'error' && chart.kind === 'missing'))
  if (stored !== null && equityOnly) {
    return (
      <div className="absolute inset-0 flex flex-col" data-panel="chart" data-equity-only="true" data-loaded="true">
        <div className="relative min-h-0 flex-1 bg-ink">
          <ErrorBoundary label="Chart">
            <QuantEquityChart points={stored} />
          </ErrorBoundary>
        </div>
      </div>
    )
  }
  return (
    <div className="absolute inset-0 flex flex-col" data-panel="chart" data-loaded={chart.status === 'ready' ? 'true' : 'false'}>
      <BarsToolbar chart={chart} />
      {chart.status === 'ready' ? (
        <ReadyChart model={chart.model} equityCurve={equityCurve} />
      ) : (
        <>
          <div className="relative min-h-0 flex-1 bg-ink">
            {chart.status === 'error' && chart.kind === 'not_ready' ? (
              <div role="status" className="absolute inset-0 flex items-center justify-center font-mono text-[12px] text-paper-50">
                The chart is written when the run finishes.
              </div>
            ) : chart.status === 'error' && chart.kind === 'missing' ? (
              <div role="status" className="absolute inset-0 flex items-center justify-center font-mono text-[12px] text-paper-50">
                {chart.message}
              </div>
            ) : chart.status === 'error' ? (
              <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-3 font-mono text-[12px] text-alert">
                <span>{chart.message}</span>
                <Button variant="secondary" size="sm" onClick={chart.retry}>
                  Retry
                </Button>
              </div>
            ) : (
              <div
                role="status"
                className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.2em] text-paper-40 uppercase"
              >
                Loading chart.json…
              </div>
            )}
          </div>
          <QuantChartScrubber
            k={0}
            total={0}
            playing={false}
            speed={5}
            window={240}
            onScrub={() => undefined}
            onTogglePlay={() => undefined}
            onSpeed={() => undefined}
            onWindow={() => undefined}
          />
        </>
      )}
    </div>
  )
}

const TICK_MS = 80
const MAX_STEP = 200

function ReadyChart({ model, equityCurve }: { model: ChartModel; equityCurve?: Props['equityCurve'] }) {
  const full = useMemo(() => chartToDraw(model, { equityCurve }), [model, equityCurve])
  const total = full?.candles.length ?? 0
  const [k, setK] = useState(Math.max(0, total - 1))
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState<QuantScrubSpeed>(5)
  // Which window button is pressed, as the chart's view reducer reports it (`null` after a manual zoom).
  const [pressed, setPressed] = useState<QuantScrubWindow | null>(240)
  const [view, setView] = useState<{ seq: number; command: ChartViewCommand }>({ seq: 0, command: { type: 'window', width: 240 } })
  // The hovered candle index (full-model index), or null while the pointer is off the chart.
  const [hover, setHover] = useState<number | null>(null)

  const active = playing && k < total - 1
  useEffect(() => {
    if (!active) return
    if (speed === 'max') {
      let id = requestAnimationFrame(function frame() {
        setK((cur) => Math.min(total - 1, cur + MAX_STEP))
        id = requestAnimationFrame(frame)
      })
      return () => cancelAnimationFrame(id)
    }
    const handle = setInterval(() => setK((cur) => Math.min(total - 1, cur + speed)), TICK_MS)
    return () => clearInterval(handle)
  }, [active, speed, total])

  const shown = useMemo(() => chartToDraw(truncateChart(model, k), { equityCurve }), [model, k, equityCurve])
  // Over a bar at or left of the scrubber bar the readout shows that bar; otherwise the scrubber bar.
  const readoutIndex = hover !== null && hover <= k ? hover : k
  const readout = useMemo(() => readoutAt(model, readoutIndex, { equityCurve }), [model, readoutIndex, equityCurve])

  function onViewChange(change: QuantViewChange) {
    const p = change.pressed
    setPressed(p === 120 || p === 240 || p === 480 || p === 'all' ? p : null)
  }
  function pressWindow(next: QuantScrubWindow) {
    setView((v) => ({ seq: v.seq + 1, command: { type: 'window', width: next } }))
  }

  function togglePlay() {
    if (active) {
      setPlaying(false)
      return
    }
    if (k >= total - 1) setK(0)
    setPlaying(true)
  }

  return (
    <>
      <div className="relative min-h-0 flex-1">
        {shown !== null ? (
          <QuantChart model={shown} view={view} initialWidth={240} totalBars={total} onViewChange={onViewChange} onHover={setHover} />
        ) : (
          <div role="status" className="absolute inset-0 flex items-center justify-center bg-ink font-mono text-[12px] text-paper-50">
            This chart has no candlestick series, so there is nothing to replay.
          </div>
        )}
      </div>
      <QuantChartScrubber
        k={k}
        total={total}
        playing={active}
        speed={speed}
        window={pressed}
        onScrub={(next) => {
          setPlaying(false)
          setK(next)
        }}
        onTogglePlay={togglePlay}
        onSpeed={setSpeed}
        onWindow={pressWindow}
      />
      {readout !== null && (
        <QuantReadout
          bar={readout.bar}
          time={readout.time}
          open={readout.open}
          high={readout.high}
          low={readout.low}
          close={readout.close}
          equity={readout.equity}
          fill={readout.fill}
          digits={readout.digits}
        />
      )}
    </>
  )
}
